import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ExternalLink } from 'lucide-react';

interface ResourceItem {
  id: string;
  image: string; // public path
  links: Record<string, string>; // per-locale URL
}

export const FeaturedFreeResources: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const code = (lang || 'en').split('-')[0];
  const { t } = useTranslation();
  const [items, setItems] = useState<ResourceItem[] | null>(null);

  useEffect(() => {
    let mounted = true;
    fetch('/free-resources.json')
      .then((r) => r.json())
      .then((data: ResourceItem[]) => {
        if (mounted) setItems(data);
      })
      .catch(() => setItems([]));
    return () => {
      mounted = false;
    };
  }, []);

  if (!items) {
    return (
      <section aria-labelledby="free-resources-heading" className="mb-16 animate-fade-in">
        <div className="h-8 w-48 bg-muted rounded mb-4" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-xl border bg-card shadow-card p-4">
              <div className="w-full h-56 bg-muted rounded-lg" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  const getLink = (res: ResourceItem) => res.links[code] || res.links['en'];

  return (
    <section aria-labelledby="free-resources-heading" className="mb-16 animate-fade-in">
      <div className="text-center mb-8">
        <h2 id="free-resources-heading" className="text-3xl font-bold mb-2">
          {t('homepage.freeResources.title', 'Featured Free Resources')}
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('homepage.freeResources.subtitle', 'Practical guides and one‑pagers to level up your employee experience.')}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {items.map((res) => {
          const href = getLink(res);
          return (
            <article key={res.id} className="group relative overflow-hidden rounded-xl border bg-gradient-card shadow-card hover:shadow-glow hover:border-primary/20 transition-all duration-300">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(`homepage.freeResources.items.${res.id}.title`) + ' — ' + t('homepage.freeResources.opensInNewTab', 'Opens in a new tab')}
                className="absolute inset-0 z-10"
              />

              <AspectRatio ratio={210/297}>
                <img
                  src={res.image}
                  alt={t(`homepage.freeResources.items.${res.id}.title`, 'Resource')}
                  loading="lazy"
                  className="w-full h-full object-cover transform transition-transform duration-500 ease-out group-hover:scale-105"
                />
              </AspectRatio>

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/96 via-background/65 to-transparent transition-opacity duration-300" />

              <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary/90 text-primary-foreground border border-primary/20 shadow-sm">
                    {t('homepage.freeResources.badge', 'Free Resource')}
                  </Badge>
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-foreground">
                  {t(`homepage.freeResources.items.${res.id}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                  {t(`homepage.freeResources.items.${res.id}.description`)}
                </p>
                <div className="mt-3">
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                    aria-label={t('homepage.freeResources.view', 'View Resource') + ' — ' + t('homepage.freeResources.opensInNewTab', 'Opens in a new tab')}
                  >
                    <Button variant="gradient" size="sm" className="pointer-events-auto shadow-md hover:shadow-lg transition-shadow duration-200">
                      {t('homepage.freeResources.view', 'View Resource')}
                      <ExternalLink className="h-4 w-4 ml-1" aria-hidden="true" />
                    </Button>
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
